import { useState } from "react";
import { motion } from "framer-motion";
import YearSearch from "@/components/YearSearch";
import EventTimeline from "@/components/EventTimeline";
import { getEventsForYear, getAllYearsWithEvents } from "@/lib/historicalEvents";
import { Calendar } from "lucide-react";

const Index = () => {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const handleYearSearch = (year: number) => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setSelectedYear(year);
      setLoading(false);
    }, 500);
  };

  const events = selectedYear ? getEventsForYear(selectedYear) : [];
  const yearsAvailable = getAllYearsWithEvents();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex flex-col">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex-1 flex flex-col items-center justify-center px-4 py-16 md:py-32"
      >
        <div className="max-w-3xl mx-auto text-center space-y-8">
          {/* Logo / Icon */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="flex justify-center"
          >
            <div className="p-4 bg-primary/10 rounded-full">
              <Calendar className="w-8 h-8 text-primary" />
            </div>
          </motion.div>

          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="space-y-2"
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              Time Travel Through History
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover the most significant events, breakthroughs, and moments
              from any year in history.
            </p>
          </motion.div>

          {/* Search Component */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <YearSearch onSearch={handleYearSearch} loading={loading} />
            {yearsAvailable.length > 0 && (
              <p className="text-xs text-muted-foreground mt-4">
                Available years: {yearsAvailable.join(", ")}
              </p>
            )}
          </motion.div>
        </div>
      </motion.section>

      {/* Events Timeline Section */}
      {selectedYear && (
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="px-4 py-12 md:py-16"
        >
          <EventTimeline year={selectedYear} events={events} />
        </motion.section>
      )}
    </div>
  );
};

export default Index;